import React from 'react';
import { Repeat } from 'lucide-react';
import { Lang, Trade, TRADES, tradesByGroup, tradeLabel } from '@/data/constants';

/**
 * GROUPED TRADE PICKERS — one place that renders the (now ~55-category)
 * marketplace catalogue grouped by service group (Home, Outdoor, Mechanical,
 * Property, Safety, Restoration, Construction). Every sign-up, alert and filter
 * surface uses these so a new category in TRADES shows up everywhere, in the
 * right group, with no second edit.
 */

interface ChipsProps {
  lang: Lang;
  selected: string[];
  onToggle: (slug: string) => void;
  /** Subset to offer (e.g. homeownerCategories()). Defaults to every trade. */
  options?: Trade[];
  /** Tailwind classes for the selected chip. */
  activeCls?: string;
  className?: string;
}

export const GroupedTradeChips: React.FC<ChipsProps> = ({
  lang,
  selected,
  onToggle,
  options = TRADES,
  activeCls = 'border-slate-900 bg-slate-900 text-white',
  className = '',
}) => (
  <div className={`space-y-3 ${className}`}>
    {tradesByGroup(options).map(({ group, trades }) => (
      <div key={group.key}>
        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
          {lang === 'es' ? group.es : group.en}
        </p>
        <div className="flex flex-wrap gap-2">
          {trades.map((t) => {
            const on = selected.includes(t.slug);
            return (
              <button
                type="button"
                key={t.slug}
                aria-pressed={on}
                onClick={() => onToggle(t.slug)}
                className={`inline-flex min-h-[36px] items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                  on ? activeCls : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'
                }`}
              >
                {tradeLabel(t.slug, lang)}
                {t.cadence !== 'project' && (
                  <Repeat className={`h-3 w-3 ${on ? 'opacity-80' : 'text-emerald-600'}`} aria-label={lang === 'es' ? 'recurrente' : 'recurring'} />
                )}
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

/** <optgroup> list for a native <select>. Render inside the <select>. */
export const GroupedTradeOptions: React.FC<{ lang: Lang; options?: Trade[] }> = ({ lang, options = TRADES }) => (
  <>
    {tradesByGroup(options).map(({ group, trades }) => (
      <optgroup key={group.key} label={lang === 'es' ? group.es : group.en}>
        {trades.map((t) => (
          <option key={t.slug} value={t.slug}>
            {tradeLabel(t.slug, lang)}
          </option>
        ))}
      </optgroup>
    ))}
  </>
);

export default GroupedTradeChips;
